import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Clock, ExternalLink, ImagePlus, Instagram, Facebook, Send, Share2, Trash2, X } from 'lucide-react';
import { PageHeader } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Checkbox, Switch, Textarea } from '@/components/ui/misc';
import { FormError } from '@/features/auth/auth-layout';
import { usePermission } from '@/hooks/use-permission';
import { ApiRequestError } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { useSocial, useSocialAccounts, useSocialMutations, useUploadAsset, type SocialPost } from './api';

const STATUS: Record<SocialPost['status'], [string, string]> = {
  DRAFT: ['Draft', 'bg-neutral-200 text-neutral-600'],
  SCHEDULED: ['Scheduled', 'bg-blue-100 text-blue-700'],
  PUBLISHING: ['Posting…', 'bg-blue-100 text-blue-700'],
  PUBLISHED: ['Posted', 'bg-emerald-100 text-emerald-700'],
  PARTIAL: ['Partly posted', 'bg-amber-100 text-amber-700'],
  FAILED: ['Failed', 'bg-red-100 text-red-700'],
};

/** Social publishing (HubSpot): Facebook pages and Instagram, now or scheduled. */
export function SocialPage() {
  const { can } = usePermission();
  const { data: accounts = [] } = useSocialAccounts();
  const { data: posts = [] } = useSocial();
  const { save, remove } = useSocialMutations();
  const upload = useUploadAsset();
  const fileInput = useRef<HTMLInputElement>(null);
  const [channelIds, setChannelIds] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [later, setLater] = useState(false);
  const [scheduledAt, setScheduledAt] = useState('');
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setMessage('');
    setImageUrl('');
    setLinkUrl('');
    setLater(false);
    setScheduledAt('');
  };

  const submit = (draft: boolean) => {
    setError(null);
    save.mutate(
      {
        channelIds,
        message,
        imageUrl: imageUrl || null,
        linkUrl: linkUrl || null,
        scheduledAt: later && scheduledAt ? new Date(scheduledAt).toISOString() : null,
        draft,
      },
      {
        onSuccess: (post) => {
          toast.success(draft ? 'Draft saved' : post.status === 'SCHEDULED' ? 'Scheduled' : post.status === 'PUBLISHED' ? 'Posted' : 'Sent — see the result below');
          reset();
        },
        onError: (failure) => setError(failure instanceof ApiRequestError ? failure.message : 'Could not post'),
      },
    );
  };

  return (
    <div>
      <PageHeader
        title="Social posts"
        description="Write once, post to your Facebook pages and Instagram accounts — now or at the best time."
        breadcrumbs={[{ label: 'Marketing hub' }, { label: 'Social posts' }]}
      />
      <div className="grid gap-5 lg:grid-cols-[1fr_24rem]">
        <div className="space-y-4">
          {can('marketing.social.create') && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">New post</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {accounts.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Connect a Facebook page or Instagram account in{' '}
                    <Link to="/inbox/channels" className="text-primary underline">
                      Inbox → Channels
                    </Link>{' '}
                    first.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {accounts.map((account) => {
                      const Icon = account.kind === 'INSTAGRAM' ? Instagram : Facebook;
                      return (
                        <label key={account.id} className="flex items-center gap-2 rounded-xl border px-3 py-2 text-sm">
                          <Checkbox
                            checked={channelIds.includes(account.id)}
                            onCheckedChange={(value) => setChannelIds(value ? [...channelIds, account.id] : channelIds.filter((id) => id !== account.id))}
                          />
                          <Icon className="h-4 w-4" style={{ color: account.kind === 'INSTAGRAM' ? '#E1306C' : '#0866FF' }} />
                          {account.name}
                        </label>
                      );
                    })}
                  </div>
                )}
                <Textarea rows={6} value={message} onChange={(event) => setMessage(event.target.value)} placeholder="What do you want to say?" />
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{message.length} / 2,200 for Instagram</span>
                </div>
                <input
                  ref={fileInput}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = '';
                    if (file) upload.mutate(file, { onSuccess: (asset) => setImageUrl(asset.url), onError: (failure) => toast.error(failure instanceof ApiRequestError ? failure.message : 'Upload failed') });
                  }}
                />
                {imageUrl ? (
                  <div className="relative inline-block">
                    <img src={imageUrl} alt="" className="max-h-48 rounded-xl border" />
                    <button onClick={() => setImageUrl('')} className="absolute end-1 top-1 rounded-full bg-black/60 p-1 text-white">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <Button variant="outline" size="sm" onClick={() => fileInput.current?.click()} disabled={upload.isPending}>
                    <ImagePlus /> {upload.isPending ? 'Uploading…' : 'Add image (needed for Instagram)'}
                  </Button>
                )}
                <Input value={linkUrl} onChange={(event) => setLinkUrl(event.target.value)} placeholder="Link (optional) — e.g. your landing page" />
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={later} onCheckedChange={setLater} /> Schedule for later
                </label>
                {later && <Input type="datetime-local" value={scheduledAt} onChange={(event) => setScheduledAt(event.target.value)} />}
                <FormError message={error} />
                <div className="flex gap-2">
                  <Button onClick={() => submit(false)} disabled={save.isPending || !channelIds.length || !message.trim() || (later && !scheduledAt)}>
                    {later ? <Clock /> : <Send />} {later ? 'Schedule' : 'Post now'}
                  </Button>
                  <Button variant="outline" onClick={() => submit(true)} disabled={save.isPending || !channelIds.length || !message.trim()}>
                    Save draft
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="space-y-3">
            {posts.map((post) => (
              <Card key={post.id}>
                <CardContent className="space-y-2 pt-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Badge className={STATUS[post.status][1]}>{STATUS[post.status][0]}</Badge>
                    <span className="text-xs text-muted-foreground">
                      {post.status === 'SCHEDULED' && post.scheduledAt ? `Goes out ${formatDateTime(post.scheduledAt)}` : formatDateTime(post.publishedAt ?? post.createdAt)}
                    </span>
                  </div>
                  <p className="whitespace-pre-wrap text-sm">{post.message}</p>
                  {post.imageUrl && <img src={post.imageUrl} alt="" className="max-h-40 rounded-lg border" />}
                  {post.results?.map((result) => (
                    <p key={result.channelId} className="text-xs">
                      {result.name}:{' '}
                      {result.error ? (
                        <span className="text-destructive">{result.error}</span>
                      ) : result.url ? (
                        <a href={result.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary underline">
                          posted <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (
                        'posted'
                      )}
                    </p>
                  ))}
                  {['DRAFT', 'SCHEDULED', 'FAILED'].includes(post.status) && (
                    <Can permission="marketing.social.delete">
                      <Button size="sm" variant="ghost" onClick={() => window.confirm('Delete this post?') && remove.mutate(post.id)}>
                        <Trash2 /> Delete
                      </Button>
                    </Can>
                  )}
                </CardContent>
              </Card>
            ))}
            {posts.length === 0 && (
              <div className="rounded-2xl border border-dashed p-10 text-center text-sm text-muted-foreground">
                <Share2 className="mx-auto mb-2 h-8 w-8 opacity-50" /> No posts yet.
              </div>
            )}
          </div>
        </div>
        <Card className="h-fit">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Good to know</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-muted-foreground">
            <p>Instagram needs a picture with every post; Facebook does not.</p>
            <p>Posting uses the same page connection as the inbox. Meta needs the pages_manage_posts and instagram_content_publish permissions on your app.</p>
            <p>Best times in the UAE: weekdays 11:00–13:00 and 19:00–22:00.</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
